// Меню действий и удаление

import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { Document } from './types';
import { isErrorStatus } from './utils';

interface DocumentActionsProps {
  anchorPosition: { top: number; left: number } | null;
  selectedDocument: Document | null;
  deleteDialogOpen: boolean;
  documentToDelete: Document | null;
  isDeleting: boolean;
  onMenuClose: () => void;
  onOpenDeleteDialog: (document: Document) => void;
  onCloseDeleteDialog: () => void;
  onConfirmDelete: () => void;
  onCreateFromDocument?: (document: Document) => void;
}

const DocumentActions: React.FC<DocumentActionsProps> = ({
  anchorPosition,
  selectedDocument,
  deleteDialogOpen,
  documentToDelete,
  isDeleting,
  onMenuClose,
  onOpenDeleteDialog,
  onCloseDeleteDialog,
  onConfirmDelete,
  onCreateFromDocument
}) => {
  const handleCreateFromDocument = () => {
    if (selectedDocument && onCreateFromDocument) {
      onCreateFromDocument(selectedDocument);
      onMenuClose();
    }
  };

  const handleOpenDeleteDialog = () => {
    if (selectedDocument) {
      onOpenDeleteDialog(selectedDocument);
    }
  };

  // Проверка: показываем "Удалить" только для черновиков (status === 9)
  const showDeleteOption = selectedDocument?.status === 9;
  // Проверка: показываем "Создать на основании" только для статусов ошибок
  const showCreateFromOption = selectedDocument && isErrorStatus(selectedDocument.status || 0);

  return (
    <>
      {/* Меню действий */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || undefined}
        open={Boolean(anchorPosition)}
        onClose={onMenuClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { minWidth: 180 }
          }
        }}
      >
        {/* Пункт "Создать на основании" - только для статусов ошибок */}
        {showCreateFromOption && (
          <MenuItem onClick={handleCreateFromDocument}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Создать на основании</ListItemText>
          </MenuItem>
        )}

        {/* Пункт "Удалить" - только для черновиков */}
        {showDeleteOption && (
          <MenuItem onClick={handleOpenDeleteDialog}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Удалить документ</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteDialogOpen}
        onClose={onCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Подтвердить удаление
        </DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить документ "{documentToDelete?.typeName} (№{documentToDelete?.id})"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onCloseDeleteDialog}
            disabled={isDeleting}
          >
            Отмена
          </Button>
          <Button
            onClick={onConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Удаление...' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentActions;

